import * as React from "react";
import { formatDate, sortAlphaNum } from "../../common/services/Utilities";
import MoreInfoButton from "../MoreInfoButton";
import { Typography, Grid, Card } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { ResultRecord } from "../../interfaces";

interface Props {
  results: ResultRecord;
}

const useStyle = makeStyles(theme => ({
  footnote: {
    fontSize: "1rem",
    margin: "12px 0 24px 0",
  },
  card: {
    "borderRadius": 6,
    "padding": 10,
    "border": "1px solid #acc8df",
    "box-shadow": "0px 0px 10px #D9E8F6",
    "& a": { lineHeight: "1.5rem" },
  },
}));
export default function BankFindOtherNames({ results }: Props) {
  const classes = useStyle();

  const tradeNames = [
    results.TE01N529,
    results.TE02N529,
    results.TE03N529,
    results.TE04N529,
    results.TE05N529,
    results.TE06N529,
  ]
    .sort(sortAlphaNum)
    .filter(Boolean);

  const priorNames = [
    results.PRIORNAME1,
    results.PRIORNAME2,
    results.PRIORNAME3,
    results.PRIORNAME4,
    results.PRIORNAME5,
    results.PRIORNAME6,
    results.PRIORNAME7,
    results.PRIORNAME8,
    results.PRIORNAME9,
    results.PRIORNAME10,
  ]
    .sort(sortAlphaNum)
    .filter(Boolean);

  return (
    <div id="bankFindOtherNames">
      <Typography variant="h4" style={{ paddingBottom: 0 }}>
        Other names and websites used by {results.NAME}
        <MoreInfoButton
          id="tradeNameInfo"
          popperPosition="left"
          label="Trade Name/URL Information"
        >
          <Typography>
            When one bank purchases another bank, the name recognition and
            reputation of the bank being purchased may be very well known and
            established in its market area. Rather than change the name of this
            bank, the bank making the purchase will continue to use the acquired
            bank's original name as a trade name, to benefit from its good name
            and reputation.
          </Typography>
          <Typography>
            In addition to having physical branches to accept deposits, many
            banks have established online deposit accepting websites (also known
            as "URLs" or "Uniform Resource Locators"). Some of these websites
            will be named similarly to the name of the bank, such as, the
            website for a bank named "ABC Bank" being named "www.abcbank.com."
            However, some banks will also have websites that have names that are
            not connected to the actual name of the bank.
          </Typography>
          <Typography>
            Please note that this information is provided electronically by the
            bank to the FDIC on the required{" "}
            <Typography
              component="a"
              href="https://fdic.gov/regulations/resources/call/Index.html"
            >
              FFIEC CALL reports
            </Typography>
            . The information is not edited by the FDIC.
          </Typography>
          <Typography>
            For more information regarding a trade name and/or URL for a
            particular bank, call the FDIC at 1-877-275-3342 and ask to speak
            with a deposit insurance specialist. If you do not see the Trade
            Name and/or URL that you are looking for, please call the FDIC.
          </Typography>
        </MoreInfoButton>
      </Typography>
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <Card id={"other-names"} className={classes.card}>
            <div id={"trade-names"} style={{ paddingBottom: "1rem" }}>
              <Typography variant="h4" style={{ paddingBottom: "0" }}>
                Trade Names *
              </Typography>
              {results.ACTIVE ? (
                tradeNames.length ? (
                  tradeNames.map(
                    (tradeName: string | undefined, index: number) =>
                      tradeName ? (
                        <Typography id={`trade-name-${index}`} key={index}>
                          {tradeName}
                        </Typography>
                      ) : (
                        undefined
                      ),
                  )
                ) : (
                  <Typography id="trade-name-0" key="0">
                    No Other Names (or Trade Names) Reported
                  </Typography>
                )
              ) : (
                <Typography id="trade-name-0" key="0">
                  This information is not available for inactive institutions.
                </Typography>
              )}
            </div>
            <div id={"prior-names"}>
              <Typography variant="h4" style={{ paddingBottom: 0 }}>
                Prior Names **
              </Typography>
              {priorNames.length ? (
                priorNames.map((priorName: string | undefined, index: number) =>
                  priorName ? (
                    <Typography id={`prior-name-${index}`} key={index}>
                      {priorName}
                    </Typography>
                  ) : (
                    undefined
                  ),
                )
              ) : (
                <Typography id="previous_name_0" key="0">
                  No Prior Names Reported
                </Typography>
              )}
            </div>
          </Card>
        </Grid>
        <Grid item md={6} xs={12}>
          <Card className={classes.card}>
            <div id={"deposit-accepting-websites"}>
              <Typography variant="h4" style={{ paddingBottom: 0 }}>
                Deposit Accepting Websites
              </Typography>
              {results.ACTIVE ? (
                <Typography id="website-0" key="0">
                  This information is currently not available.
                </Typography>
              ) : (
                <Typography id="website-0" key="0">
                  This information is not available for inactive institutions.
                </Typography>
              )}
            </div>
          </Card>
        </Grid>
        <Grid item xs={12}>
          {results.ACTIVE ? (
            <>
              <Typography id="risview-report-date" className={classes.footnote}>
                *Data reported as of {formatDate(results.RISDATE)}. Trade Names
                are presented as reported by the institution on Schedule RC-M of
                the FFIEC Call Report, with no system edits.
              </Typography>
            </>
          ) : (
            <Typography className={classes.footnote} id="risview-report-date">
              *This information is reported on the current Schedule RC-M of the
              FFIEC Call report, and is therefore not available for inactive
              institutions.
            </Typography>
          )}
          <Typography style={{ fontSize: "1rem" }} id="weekly-report-date">
            **Data reported as of {formatDate(results.RUNDATE)}. Please refer to
            the History tab to determine if the institution was FDIC-insured
            under that prior name.
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
}
